import Markdown from "@/components/Markdown";
import { AlertTriangleIcon, Stars01Icon } from "@/icons";
import { Badge } from "@/kit/ui/badge";
import { Button } from "@/kit/ui/button";
import { useAuth } from "@/utils/auth";
import { Suspense, useEffect, useState, useTransition } from "react";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { graphql, useMutation, useRefetchableFragment } from "react-relay";
import {
  PaperAssessmentFragment$key,
  PaperFlagKind,
} from "./__generated__/PaperAssessmentFragment.graphql";
import { PaperAssessmentRefetchQuery } from "./__generated__/PaperAssessmentRefetchQuery.graphql";
import { PaperAssessmentRequestMutation } from "./__generated__/PaperAssessmentRequestMutation.graphql";

const POLL_INTERVAL_MS = 5000;
/** Long enough for a session; after that, the reader is asked to come back. */
const POLL_LIMIT_MS = 10 * 60 * 1000;

const Fragment = graphql`
  fragment PaperAssessmentFragment on Paper
  @refetchable(queryName: "PaperAssessmentRefetchQuery") {
    id
    version
    assessmentRequestable
    assessment {
      status
      summary
      aiLikelihood
      aiUseDisclosed
      aiDisclosureQuote
      fullText
      model
      arxivVersion
      flags {
        kind
        detail
      }
    }
  }
`;

const RequestMutation = graphql`
  mutation PaperAssessmentRequestMutation($id: ID!) {
    requestPaperAssessment(id: $id) {
      ...PaperAssessmentFragment
    }
  }
`;

const AI_LIKELIHOOD = defineMessages({
  0: { defaultMessage: "No sign of AI-generated text" },
  1: { defaultMessage: "Little sign of AI-generated text" },
  2: { defaultMessage: "Some signs of AI-generated text" },
  3: { defaultMessage: "Likely largely AI-generated" },
  4: { defaultMessage: "Almost certainly largely AI-generated" },
});

const FLAGS = defineMessages<Exclude<PaperFlagKind, "%future added value">>({
  OVERCLAIMS: { defaultMessage: "Overclaims" },
  INCREMENTAL: { defaultMessage: "Incremental" },
  SMALL_NUMERICS_ONLY: { defaultMessage: "Small numerics only" },
  UNCLEAR_NOVELTY: { defaultMessage: "Unclear novelty" },
  MISSING_COMPARISON: { defaultMessage: "Missing comparison" },
  STRONG_ASSUMPTIONS: { defaultMessage: "Strong assumptions" },
  UNCLEAR_WRITING: { defaultMessage: "Unclear writing" },
  POSSIBLE_ERROR: { defaultMessage: "Possible error" },
  CLAIM_FAILS: { defaultMessage: "Central claim may not hold" },
  LOCAL_ERROR: { defaultMessage: "Error found" },
});

export interface PaperAssessmentProps {
  paper: PaperAssessmentFragment$key;
  /** The earlier arXiv version being shown, if not the latest. */
  viewedVersion?: number;
}

/** The LLM's summary of the paper, with what it noticed about the text. */
export default function PaperAssessment(props: PaperAssessmentProps) {
  const intl = useIntl();
  const { isAuthenticated } = useAuth();
  const [paper, refetch] = useRefetchableFragment<
    PaperAssessmentRefetchQuery,
    PaperAssessmentFragment$key
  >(Fragment, props.paper);
  const [request, requesting] =
    useMutation<PaperAssessmentRequestMutation>(RequestMutation);
  const [, startTransition] = useTransition();
  const [requestError, setRequestError] = useState<string>();
  const [stoppedPolling, setStoppedPolling] = useState(false);
  const assessment = paper.assessment;
  const pending =
    assessment?.status === "QUEUED" || assessment?.status === "RUNNING";

  useEffect(() => {
    if (!pending) return;
    setStoppedPolling(false);
    const interval = setInterval(
      () =>
        startTransition(() => {
          refetch({}, { fetchPolicy: "network-only" });
        }),
      POLL_INTERVAL_MS,
    );
    const limit = setTimeout(() => {
      clearInterval(interval);
      setStoppedPolling(true);
    }, POLL_LIMIT_MS);
    return () => {
      clearInterval(interval);
      clearTimeout(limit);
    };
  }, [pending, refetch]);

  const requestButton = isAuthenticated && paper.assessmentRequestable && (
    <Button
      type="button"
      variant="secondary"
      size="sm"
      className="self-start"
      disabled={requesting}
      onClick={() => {
        setRequestError(undefined);
        request({
          variables: { id: paper.id },
          onError: (error) => setRequestError(error.message),
        });
      }}
    >
      <Stars01Icon className="w-4 h-4" />
      {assessment?.status === "FAILED" ? (
        <FormattedMessage defaultMessage="Try the summary again" />
      ) : (
        <FormattedMessage defaultMessage="Generate summary" />
      )}
    </Button>
  );

  // An earlier version only shows a summary written from it.
  if (
    props.viewedVersion !== undefined &&
    (assessment?.status !== "DONE" ||
      assessment.arxivVersion !== props.viewedVersion)
  ) {
    return null;
  }

  if (!assessment || assessment.status === "FAILED") {
    if (!requestButton) return null;
    return (
      <section className="flex flex-col gap-2">
        <h2 className="text-grey-900 text-xl font-semibold">
          <FormattedMessage defaultMessage="AI Summary" />
        </h2>
        <p className="text-grey-600 text-sm">
          {assessment?.status === "FAILED" ? (
            <FormattedMessage defaultMessage="The summary could not be written." />
          ) : (
            <FormattedMessage defaultMessage="An LLM can read the paper and summarise it. This takes a few minutes." />
          )}
        </p>
        {requestButton}
        {requestError && (
          <p role="alert" className="text-error-600 text-sm">
            {requestError}
          </p>
        )}
      </section>
    );
  }

  if (pending) {
    return (
      <section className="flex flex-col gap-2">
        <h2 className="text-grey-900 text-xl font-semibold">
          <FormattedMessage defaultMessage="AI Summary" />
        </h2>
        <p
          className={
            stoppedPolling
              ? "text-grey-600 text-sm"
              : "text-grey-600 text-sm animate-pulse"
          }
        >
          {stoppedPolling ? (
            <FormattedMessage defaultMessage="This is taking longer than usual. Come back later to read the summary." />
          ) : assessment.status === "QUEUED" ? (
            <FormattedMessage defaultMessage="Queued: an LLM will read the paper as soon as there is capacity." />
          ) : (
            <FormattedMessage defaultMessage="An LLM is reading the paper…" />
          )}
        </p>
      </section>
    );
  }

  const flags = assessment.flags.filter(
    (flag): flag is typeof flag & { kind: keyof typeof FLAGS } =>
      flag.kind in FLAGS,
  );
  const basedOn =
    props.viewedVersion === undefined &&
    assessment.arxivVersion != null &&
    assessment.arxivVersion < (paper.version ?? 0)
      ? assessment.arxivVersion
      : undefined;

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-baseline gap-x-3">
        <h2 className="text-grey-900 text-xl font-semibold">
          <FormattedMessage defaultMessage="AI Summary" />
        </h2>
        {basedOn !== undefined && (
          <span className="text-grey-500 text-sm">
            <FormattedMessage
              defaultMessage="Based on v{version}"
              values={{ version: basedOn }}
            />
          </span>
        )}
      </div>
      {assessment.summary && (
        <Suspense
          fallback={<p className="whitespace-pre-line">{assessment.summary}</p>}
        >
          <Markdown className="text-grey-700">{assessment.summary}</Markdown>
        </Suspense>
      )}
      <div className="flex flex-col gap-2 rounded-lg border border-grey-200 bg-grey-50 p-4 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          {assessment.aiLikelihood != null &&
            assessment.aiLikelihood in AI_LIKELIHOOD && (
              <Badge
                size="sm"
                variant={assessment.aiLikelihood >= 3 ? "warning" : "default"}
              >
                {intl.formatMessage(
                  AI_LIKELIHOOD[assessment.aiLikelihood as 0 | 1 | 2 | 3 | 4],
                )}
              </Badge>
            )}
          {assessment.aiUseDisclosed && (
            <Badge size="sm">
              <FormattedMessage defaultMessage="AI use disclosed" />
            </Badge>
          )}
          {flags
            .filter((flag) => !flag.detail)
            .map((flag) => (
              <Badge key={flag.kind} size="sm" variant="warning">
                {intl.formatMessage(FLAGS[flag.kind])}
              </Badge>
            ))}
        </div>
        {assessment.aiUseDisclosed && assessment.aiDisclosureQuote && (
          <blockquote className="border-l-2 border-grey-300 pl-3 text-grey-600 italic">
            {assessment.aiDisclosureQuote}
          </blockquote>
        )}
        {flags
          .filter((flag) => flag.detail)
          .map((flag) => (
            <div key={flag.kind} className="flex gap-2 text-grey-700">
              <AlertTriangleIcon className="w-4 h-4 shrink-0 mt-0.5 text-warning-500" />
              <p className="whitespace-pre-line">
                <span className="font-semibold">
                  {intl.formatMessage(FLAGS[flag.kind])}:
                </span>{" "}
                {flag.detail}
              </p>
            </div>
          ))}
        <p className="text-grey-500 text-xs">
          {assessment.fullText ? (
            <FormattedMessage
              defaultMessage="Written by {model} from the paper's full text. Automated: it can be wrong."
              values={{ model: assessment.model }}
            />
          ) : (
            <FormattedMessage
              defaultMessage="Written by {model} from the abstract only. Automated: it can be wrong."
              values={{ model: assessment.model }}
            />
          )}
        </p>
      </div>
    </section>
  );
}
